import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/lib/utils";

type ScrollEdges = "none" | "top" | "bottom" | "both";

const FADE_CLASSES = {
  sidebar: { from: "from-sidebar", icon: "text-sidebar-foreground/50 hover:text-sidebar-foreground/80" },
  background: { from: "from-background", icon: "text-foreground/50 hover:text-foreground/80" },
} as const;

interface ScrollIndicatorAreaProps extends React.ComponentProps<"div"> {
  /** Surface the area sits on; the edge fades blend into it. */
  surface?: keyof typeof FADE_CLASSES;
  /** Classes for the positioned wrapper around the scroll container. */
  wrapperClassName?: string;
}

/**
 * Vertical scroll container without a visible scrollbar. A fade with a
 * bouncing chevron at the top and/or bottom edge shows that more content is
 * hidden in that direction; clicking the chevron scrolls by 100px.
 */
const ScrollIndicatorArea = React.forwardRef<HTMLDivElement, ScrollIndicatorAreaProps>(
  ({ className, wrapperClassName, surface = "sidebar", ...props }, ref) => {
    const scrollRef = React.useRef<HTMLDivElement | null>(null);
    const [edges, setEdges] = React.useState<ScrollEdges>("none");

    // Merge forwarded ref with internal ref
    const mergedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        scrollRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    React.useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;

      const update = () => {
        const { scrollTop, scrollHeight, clientHeight } = el;
        const atTop = scrollTop <= 1;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

        if (scrollHeight <= clientHeight) setEdges("none");
        else if (atTop) setEdges("bottom");
        else if (atBottom) setEdges("top");
        else setEdges("both");
      };

      el.addEventListener("scroll", update, { passive: true });
      const ro = new ResizeObserver(update);
      ro.observe(el);
      // Content changes (e.g. a filtered list) alter scrollHeight without resizing the container.
      const mo = new MutationObserver(update);
      mo.observe(el, { childList: true, subtree: true });
      update();

      return () => {
        el.removeEventListener("scroll", update);
        ro.disconnect();
        mo.disconnect();
      };
    }, []);

    const showTop = edges === "top" || edges === "both";
    const showBottom = edges === "bottom" || edges === "both";
    const fade = FADE_CLASSES[surface];

    const scrollBy = React.useCallback((delta: number) => {
      scrollRef.current?.scrollBy({ top: delta, behavior: "smooth" });
    }, []);

    return (
      <div className={cn("relative flex min-h-0 flex-1 flex-col", wrapperClassName)}>
        {/* Top scroll indicator */}
        <div
          data-testid="scroll-indicator-top"
          data-visible={showTop}
          className={cn(
            "absolute inset-x-0 top-0 z-10 flex items-center justify-center bg-gradient-to-b from-30% to-transparent transition-all duration-200",
            fade.from,
            showTop ? "h-8 opacity-100" : "pointer-events-none h-0 opacity-0"
          )}
        >
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => scrollBy(-100)}
            className={cn("flex cursor-pointer items-center justify-center transition-colors", fade.icon)}
          >
            <ChevronUp className="h-4 w-4 animate-bounce" />
          </button>
        </div>
        <div
          ref={mergedRef}
          className={cn(
            "flex min-h-0 flex-1 flex-col overflow-auto",
            "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
            className
          )}
          {...props}
        />
        {/* Bottom scroll indicator */}
        <div
          data-testid="scroll-indicator-bottom"
          data-visible={showBottom}
          className={cn(
            "absolute inset-x-0 bottom-0 z-10 flex items-center justify-center bg-gradient-to-t from-30% to-transparent transition-all duration-200",
            fade.from,
            showBottom ? "h-8 opacity-100" : "pointer-events-none h-0 opacity-0"
          )}
        >
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => scrollBy(100)}
            className={cn("flex cursor-pointer items-center justify-center transition-colors", fade.icon)}
          >
            <ChevronDown className="h-4 w-4 animate-bounce" />
          </button>
        </div>
      </div>
    );
  }
);
ScrollIndicatorArea.displayName = "ScrollIndicatorArea";

export { ScrollIndicatorArea };
